<template>
  <div
    class="webtotext-form text-center pb-0 mb-3"
    :class="{
      'rounded-lg' : !isMobile,
      'webtotext-form__sent': messageSentSuccessOrFailed
    }"
  >
    <div
      class="webtotext-form__header"
      :class="{ 'rounded-t-lg': !isMobile, 'd-none d-sm-flex': messageSentSuccessOrFailed && isMobile }"
    >
      <div class="webtotext-form__header-wrapper pa-8 d-flex justify-space-between">
        <div class="webtotext-form__header-description">
          <h2
            class="text-left"
            v-text="formHeaderTitle"
          />
          <h5
            class="text-left mt-2"
            v-text="formHeaderDescription"
          />
        </div>
        <pp-button
          type="secondary"
          class="webtotext-form__header-btn-close d-flex"
          icon="close"
          @click="onFormClose(messageSentSuccessOrFailed)"
        />
      </div>
    </div>
    <div
      v-if="messageSentSuccessOrFailed && isMobile"
      class="webtotext-form__message-sent-mobile-close align-start mb-auto pt-7 px-7 text-right"
    >
      <pp-button
        type="secondary"
        class="webtotext-form__header-btn-close"
        icon="close"
        @click="onFormClose(true)"
      />
    </div>
    <div
      class="webtotext-form__wrapper pa-8 rounded-b-lg"
      :class="{ 'd-flex flex-column justify-center webtotext-form__wrapper--sent': isMobile && messageSentSuccessOrFailed }"
    >
      <!-- Form  -->
      <div v-if="!messageSentSuccess && !messageSentFailed">
        <form
          ref="form"
          :value="isValid"
          @submit.prevent="onSubmit"
        >
          <div class="webtotext-form__wrapper-inner white elevation-24 rounded pa-6 pb-0 mb-4">
            <div class="webtotext-form__wrapper-fields">
              <pp-input-select
                v-if="locations && locations.length > 1"
                v-model="locationPhoneNumber"
                :items="locations"
                item-text="name"
                item-value="provisionedNumber"
                :items-prepend-icon="'location'"
                :error-messages="locationErrors"
                clearable="clearable"
                placeholder="Select a location"
                @blur="onBlur('locationPhoneNumber')"
              />
              <pp-input-select
                v-model="patientType"
                :items="patientTypeItems"
                :error-messages="patientTypeErrors"
                clearable="clearable"
                placeholder="New or returning patient?"
                @blur="onBlur('patientType')"
              />
              <div class="d-flex">
                <pp-input-text
                  v-model="firstName"
                  placeholder="First Name"
                  :error-messages="firstNameErrors"
                  :disabled="isLoading"
                  class="mr-3"
                  @blur="onBlur('firstName')"
                />
                <pp-input-text
                  v-model="lastName"
                  placeholder="Last Name"
                  :error-messages="lastNameErrors"
                  :disabled="isLoading"
                  @blur="onBlur('lastName')"
                />
              </div>
              <pp-input-text
                v-model="mobileNumber"
                v-mask="'(###) ### ####'"
                placeholder="(•••) ••• ••••"
                :error-messages="mobileNumberErrors"
                :disabled="isLoading"
                type="tel"
                @blur="onBlur('mobileNumber')"
              />
              <pp-input-text
                v-model="email"
                placeholder="Email address"
                :error-messages="emailErrors"
                :disabled="isLoading"
                type="email"
                @blur="onBlur('email')"
              />
              <pp-input-textarea
                v-model="message"
                class="webtotext-form__textarea"
                placeholder="Send a message..."
                :error-messages="messageErrors"
                :disabled="isLoading"
                label=""
                @blur="onBlur('message')"
              />
            </div>
          </div>
          <h6 class="webtotext-form__text">
            By clicking Send, I agree to the
            <a
              :href="privacyPolicyLink"
              target="_blank"
            >
              Privacy Policy
            </a> and
            <a
              :href="termsOfUseLink"
              target="_blank"
            >Terms of Use</a>,
            and I consent to receiving text messages from this practice acknowledging that data rates may apply. Message frequency varies.
          </h6>
          <div class="d-flex flex-column mt-3">
            <pp-button
              type="primary"
              :class="`elevation-24 webtotext-form__wrapper-submit-btn${isValid ? '' : '--disabled'}`"
              :disabled="!isValid"
              text="Send"
              submit="submit"
            />
          </div>
        </form>
      </div>

      <!-- Message sent success or error state -->
      <div
        v-else
      >
        <div
          class="webtotext-form__wrapper-message-sent white"
          :class="{
            'pt-12 px-10 mb-3 mb-4': !isMobile,
            'pb-5': !messageSentFailed,
            'pb-12': messageSentFailed
          }"
        >
          <div
            class="webtotext-form__wrapper-message-sent-icon-wrapper rounded-circle mx-auto my-auto d-flex"
            :class="{
              'green darken-1': !messageSentFailed,
              'red darken-1': messageSentFailed
            }"
          >
            <i
              class="d-flex mx-auto my-auto white--text"
              :class="{
                'pp-icon-close' : messageSentFailed,
                'pp-icon-check' : !messageSentFailed
              }"
            />
          </div>
          <h3
            class="mt-3"
            v-text="messageSentFailed ? messageFailedBodyTitle : messageSentSuccessTitle"
          />
          <p
            v-if="!messageSentFailed"
            class="mt-1"
          >
            {{ messageSentSuccessDescription }}
            <span
              class="font-weight-black"
            >
              {{ locationPhoneNumber | vMaskFilter(phoneNumberMask) }}.
            </span>
          </p>
          <template
            v-if="!messageSentFailed"
          >
            <h4
              class="mt-7 webtotext-form__wrapper-message-sent__footer text-subtitle-1"
            >
              {{ messageSentSuccessInquiriesDescription }} {{ locationInquiryNumber | vMaskFilter(phoneNumberMask) }}.
            </h4>
            <h4
              class="webtotext-form__wrapper-message-sent__footer text-subtitle-1"
            >
              {{ messageSentSuccessEmergenciesDescription }}
            </h4>
          </template>
        </div>
        <div class="d-flex flex-column mt-3">
          <pp-button
            type="primary"
            class="elevation-24"
            text="Close"
            @click="onFormClose(true)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VueMaskDirective, VueMaskFilter } from 'v-mask'
import { validationMixin } from 'vuelidate'
import { required, email, helpers } from 'vuelidate/lib/validators'
import { WebToTextConstants } from '@/constants'
import { mapActions, mapState } from 'vuex'
import { RecaptchaException } from '@/exceptions/RecaptchaException'
const mobileNumberRegex = /^\(\d{3}\) \d{3} \d{4}$/
const mobileNumber = helpers.regex('mobileNumber', mobileNumberRegex)

export default {
  name: 'WebToTextForm',
  directives: {
    mask: VueMaskDirective
  },
  filters: {
    vMaskFilter: VueMaskFilter
  },
  mixins: [validationMixin],
  props: {},
  data () {
    return {
      isLoading: false,
      locationPhoneNumber: null,
      locationInquiryNumber: null,
      patientType: null,
      firstName: null,
      lastName: null,
      email: null,
      mobileNumber: null,
      message: null,
      termsAccepted: false,
      messageSentSuccess: false,
      messageSentFailed: false,
      messageSentSuccessTitle: WebToTextConstants.MESSAGE_SENT_SUCCESS_TITLE,
      messageSentSuccessDescription: WebToTextConstants.MESSAGE_SENT_SUCCESS_DESCRIPTION,
      messageSentSuccessInquiriesDescription: WebToTextConstants.MESSAGE_SENT_SUCCESS_INQUIRIES_DESCRIPTION,
      messageSentSuccessEmergenciesDescription: WebToTextConstants.MESSAGE_SENT_SUCCESS_EMERGENCIES_DESCRIPTION,
      messageFailedBodyTitle: WebToTextConstants.MESSAGE_FAILED_BODY_TITLE,
      windowResizeTimer: null,
      phoneNumberMask: WebToTextConstants.PHONE_NUMBER_MASK
    }
  },
  computed: {
    ...mapState({
      locations: 'locations',
      isMobile: 'isMobile'
    }),
    secondaryColor () {
      return `#${this.$ppColors.secondary}`
    },
    termsOfUseLink () {
      return `${this.$practiceDomain}/our-terms`
    },
    privacyPolicyLink () {
      return `${this.$practiceDomain}/your-privacy`
    },
    /**
     * Desktop view form has a header that shows different title based on the form state.
     * 1. Message not sent
     * 2. Message Sent but message submission failed
     * 3. Message Sent and message successfully sent
     * formHeaderTitle checks for messageSentFailed and sets the WebToTextConstants.MESSAGE_FAILED_HEADER_TITLE header on true
     * If false, checks for messageSentSuccess and sets the WebToTextConstants.MESSAGE_SENT_HEADER_TITLE on true
     * If false, the fallback is WebToTextConstants.HEADER_TITLE, the normal header before the form submission
    */
    formHeaderTitle () {
      return this.messageSentFailed ? WebToTextConstants.MESSAGE_FAILED_HEADER_TITLE
        : this.messageSentSuccess ? WebToTextConstants.MESSAGE_SENT_HEADER_TITLE
          : WebToTextConstants.HEADER_TITLE
    },
    /**
     * Desktop view form has a header that shows different description based on the form state.
     * 1. Message not sent
     * 2. Message Sent but message submission failed
     * 3. Message Sent and message successfully sent
     * formHeaderTitle checks for messageSentFailed and sets the WebToTextConstants.MESSAGE_FAILED_HEADER_DESCRIPTION header on true
     * If false, checks for messageSentSuccess and sets the WebToTextConstants.MESSAGE_SENT_HEADER_DESCRIPTION on true
     * If false, the fallback is the WebToTextConstants.HEADER_DESCRIPTION, the normal header before the form submission
    */
    formHeaderDescription () {
      return this.messageSentFailed ? WebToTextConstants.MESSAGE_FAILED_HEADER_DESCRIPTION
        : this.messageSentSuccess ? WebToTextConstants.MESSAGE_SENT_HEADER_DESCRIPTION
          : WebToTextConstants.HEADER_DESCRIPTION
    },
    messageSentSuccessOrFailed () {
      return this.messageSentSuccess || this.messageSentFailed
    },
    isValid () {
      return !this.$v.$invalid
    },
    locationErrors () {
      return this.generateRequiredFieldErrors('locationPhoneNumber', 'A location')
    },
    patientTypeErrors () {
      return this.generateRequiredFieldErrors('patientType', 'Patient Type')
    },
    firstNameErrors () {
      return this.generateRequiredFieldErrors('firstName', 'First name')
    },
    lastNameErrors () {
      return this.generateRequiredFieldErrors('lastName', 'Last name')
    },
    emailErrors () {
      const requiredErrors = this.generateRequiredFieldErrors('email', 'Email')

      if (requiredErrors.length) {
        return requiredErrors
      }
      if (this.$v.email.$dirty && !this.$v.email.email) {
        return ['Please enter a valid email address']
      }
      return []
    },
    mobileNumberErrors () {
      const requiredErrors = this.generateRequiredFieldErrors('mobileNumber', 'Mobile number')

      if (requiredErrors.length) {
        return requiredErrors
      }
      if (this.$v.mobileNumber.$dirty && !this.$v.mobileNumber.mobileNumber) {
        return ['Please enter a valid mobile number']
      }
      return []
    },
    messageErrors () {
      return this.generateRequiredFieldErrors('message', 'A message')
    },
    patientTypeItems () {
      return Object.values(WebToTextConstants.PATIENT_TYPE)
        .map(type => {
          return {
            text: type
          }
        })
    }
  },
  async mounted () {
    this.setPracticeLocation()
    // Wait for recaptcha script to be loaded
    await this.$recaptchaLoaded()
    // Once loaded, hide recaptcha badge
    this.$recaptchaInstance.hideBadge()
  },
  methods: {
    ...mapActions({
      sendMessage: 'sendMessage'
    }),
    setPracticeLocation () {
      if (this.locations && this.locations.length === 1) {
        this.locationPhoneNumber = this.locations[0].provisionedNumber
        this.locationInquiryNumber = this.locations[0].inquiryPhone
        this.location = this.locations[0].provisionedNumber
      }
    },
    onFormClose (resetForm) {
      this.$emit('closeForm')

      if (resetForm) {
        // reset message sent status
        this.messageSentSuccess = false
        this.messageSentFailed = false

        // reset form data
        this.locationPhoneNumber = null
        this.patientType = null
        this.firstName = null
        this.lastName = null
        this.mobileNumber = null
        this.email = null
        this.message = null

        // set practice location if its single location
        this.setPracticeLocation()

        // reset validation
        this.$v.$reset()
      }
    },
    generateRequiredFieldErrors (fieldKey, errorMessagePrefix) {
      return this.$v[fieldKey].$dirty && !this.$v[fieldKey].required
        ? [`${errorMessagePrefix} is required`] : []
    },
    async onSubmit () {
      this.isLoading = true

      try {
        const token = await this.validateRecaptcha()

        const selectedLocation = this.locations.filter((location) => {
          return location.provisionedNumber === this.locationPhoneNumber
        })

        this.locationInquiryNumber = selectedLocation[0].inquiryPhone

        await this.submitForm({
          locationNumber: this.locationPhoneNumber,
          firstName: this.firstName,
          lastName: this.lastName,
          mobileNumber: this.mobileNumber,
          email: this.email,
          message: this.message,
          patientType: this.patientType,
          practiceUuid: this.$practiceUuid,
          locationUuid: selectedLocation[0].locationUuid || ''
        }, token)
        this.messageSentSuccess = true
      } catch (err) {
        this.messageSentFailed = true
        this.$v.$touch()

        if (err instanceof RecaptchaException) {
          this.trackGA({ action: 'recaptcha-failed' })
        } else {
          this.trackGA({ action: 'api-error', label: 'save_incoming_sms_v2' })
        }
      }

      this.isLoading = false
    },
    async validateRecaptcha () {
      const token = await this.$recaptcha('submit')

      if (!token) {
        throw new RecaptchaException()
      }
      return token
    },
    submitForm (emailData, recaptchaToken) {
      this.trackGA({ action: 'form-submit', label: this.patientType })
      return this.sendMessage({ message: emailData, token: recaptchaToken })
    },
    onBlur (field) {
      this.$v[field].$touch()
      if (this.$v[field].$invalid) {
        this.trackGA({ action: 'form-input-error', label: field })
      }
      this.trackGA({ action: 'form-input', label: field })
    }
  },
  validations: {
    locationPhoneNumber: { required },
    patientType: { required },
    firstName: { required },
    lastName: { required },
    email: { required, email },
    mobileNumber: { required, mobileNumber },
    message: { required }
  }
}
</script>
<style lang="scss" scoped>
  $header-height: 162px;
  $padding-spacing: 64px;

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: $color-synapse-2;
    border-radius: 4px;
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, .5);
  }

  .webtotext-form {
    box-shadow: $box-shadow;
    width: 430px;

    @include mobile() {
      height: var(--pp-parent-height, 100vh);
      width: var(--pp-parent-width, 100vw);
    }

    &__sent {
      @include mobile() {
        background: $white;
        height: var(--pp-parent-height, 100vh);
      }
    }

    &__header {
      background-color: var(--pp-theme-color, #{$color-holistic});
      color: var(--pp-primary-button-text-custom-color, #{$color-synapse-2});
      height: $header-height;

      @include mobile() {
        width: var(--pp-parent-width, 100vw);
      }
    }

    &__header-btn-close {
      background-color: var(--pp-theme-color);
      border: none;

      ::v-deep {
        i {
          color: $white;
          font-size: 1.4rem;
        }
      }
    }

    &__wrapper {
      background: $white;

      &--sent {
        height: calc(var(--pp-parent-height, 100vh));
      }

      &-inner {
        margin-top: -75px;

        @include mobile() {
          width: calc(var(--pp-parent-width, 100vw) - #{$padding-spacing});
        }

        @include desktop() {
          height: auto;
          max-height: calc(var(--pp-parent-height, 100vh) * .5);
          min-height: 140px;
          overflow-x: hidden;
          overflow-y: scroll;
        }
      }

      &-message-sent {
        margin-top: -75px;

        @include desktop () {
          border-radius: 4px;
          box-shadow: $box-shadow;
        }

        &-icon-wrapper,
        &-mobile-icon-wrapper {
          height: 44px;
          width: 44px;
        }

        &__footer {
          color: $color-synapse-2;
        }
      }
    }

    &__message-sent-mobile {
      height: var(--pp-parent-height, 100vh);

      &-close {
        width: var(--pp-parent-width, 100vw);

        .v-btn {
          background-color: $white;
          border: $white;
          color: $color-synapse-2;
        }
      }
    }

    &__wrapper-message-sent-mobile {
      .v-btn {
        background-color: $color-synapse-1;
        color: $white;
      }
    }

    &__text {
      color: $color-synapse-2;

      a {
        color: inherit;

        &:hover {
          color: $color-synapse-2;
        }
      }
    }

    &__textarea {
      ::v-deep {
        textarea {
          max-height: 95px;
        }
      }
    }
  }
</style>
